import React, { useState, useEffect } from 'react';
import { timeOffAPI } from '../../services/api';
import './VacationRequest.css';
import { 
  Palmtree, 
  AlertCircle, 
  Heart, 
  Target, 
  HelpCircle,
  CheckCircle,
  XCircle,
  Clock,
  Info,
  X
} from 'lucide-react';

interface VacationSummary {
  totalVacationDays: number;
  usedVacationDays: number;
  pendingVacationDays: number;
  remainingVacationDays: number;
  year: number;
  user: {
    firstName: string;
    lastName: string;
    weeklyHours: number;
    workDays: string;
  };
}

interface TimeOffRequest {
  id: number;
  startDate: string;
  endDate: string;
  type: string;
  status: string;
  reason: string | null;
  workingDays: number;
  createdAt: string;
  substitute?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  approver?: {
    firstName: string;
    lastName: string;
  };
  approvedAt?: string;
  adminNotes?: string;
}

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

interface VacationRequestProps {
  user: any;
}

interface SickDaysSummary {
  totalSickDays: number;
  sickDayEntries: number;
  year: number;
  sickDays: Array<{
    id: number;
    startDate: string;
    endDate: string;
    workingDays: number;
    reason: string | null;
    doctorNote: boolean;
  }>;
  user: {
    firstName: string;
    lastName: string;
    weeklyHours: number;
    workDays: string;
  };
}
export const VacationRequest: React.FC<VacationRequestProps> = ({}) => {
  const [activeTab, setActiveTab] = useState<'request' | 'overview' | 'sick-summary'>('request');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    reason: '',
    substituteUserId: ''
  });

  const [vacationSummary, setVacationSummary] = useState<VacationSummary | null>(null);
  const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[]>([]);
  const [availableSubstitutes, setAvailableSubstitutes] = useState<User[]>([]);
  const [calculatedDays, setCalculatedDays] = useState<number | null>(null);
  const [sickDaysSummary, setSickDaysSummary] = useState<SickDaysSummary | null>(null);
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [holidays, setHolidays] = useState<Array<{date: string, name: string}>>([]);
  const [validationMessage, setValidationMessage] = useState<string>('');

  useEffect(() => {
    loadVacationSummary();
    loadTimeOffRequests();
    loadSickDaysSummary();
  }, []);

  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      calculateWorkingDays();
      loadAvailableSubstitutes();
      loadBlockedDates();
      loadHolidays();
    } else {
      setCalculatedDays(null);
      setAvailableSubstitutes([]);
      setBlockedDates([]);
      setHolidays([]);
      setValidationMessage('');
    }
  }, [formData.startDate, formData.endDate]);

  useEffect(() => {
    // Lade blocked dates für die nächsten 2 Jahre für den Datepicker
    const today = new Date();
    const nextYear = new Date();
    nextYear.setFullYear(today.getFullYear() + 2);
    
    loadBlockedDatesForDatePicker(today.toISOString().split('T')[0], nextYear.toISOString().split('T')[0]);
  }, []);

  const loadVacationSummary = async () => {
    try {
      const response = await timeOffAPI.getVacationSummary();
      setVacationSummary(response.data);
    } catch (error) {
      console.error('Error loading vacation summary:', error);
    }
  };

  const loadTimeOffRequests = async () => {
    try {
      const response = await timeOffAPI.getTimeOffRequests();
      setTimeOffRequests(response.data.timeOffs);
    } catch (error) {
      console.error('Error loading time off requests:', error);
    }
  };

  const loadAvailableSubstitutes = async () => {
    try {
      const response = await timeOffAPI.getAvailableSubstitutes({
        startDate: formData.startDate,
        endDate: formData.endDate
      });
      setAvailableSubstitutes(response.data.users);
    } catch (error) {
      console.error('Error loading substitutes:', error);
    }
  };

  const loadSickDaysSummary = async () => {
    try {
      const response = await timeOffAPI.getSickDaysSummary();
      setSickDaysSummary(response.data);
    } catch (error) {
      console.error('Error loading sick days summary:', error);
    }
  };

  const loadBlockedDates = async () => {
    try {
      const response = await timeOffAPI.getBlockedDates({
        startDate: formData.startDate,
        endDate: formData.endDate
      });
      setBlockedDates(response.data.blockedDates);
    } catch (error) {
      console.error('Error loading blocked dates:', error);
    }
  };

  const loadHolidays = async () => {
    try {
      const response = await timeOffAPI.getHolidaysInRange({
        startDate: formData.startDate,
        endDate: formData.endDate
      });
      setHolidays(response.data.holidays);
    } catch (error) {
      console.error('Error loading holidays:', error);
    }
  };

  const calculateWorkingDays = () => {
    if (!formData.startDate || !formData.endDate || !vacationSummary) return;

    const startDate = new Date(formData.startDate);
    const endDate = new Date(formData.endDate);
    const userWorkDays = vacationSummary.user.workDays.split(',').map(d => parseInt(d));

    let workingDays = 0;
    let blockedDaysFound: string[] = [];
    let holidaysFound: string[] = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const dayOfWeek = currentDate.getDay();
      const normalizedDay = dayOfWeek === 0 ? 7 : dayOfWeek; // Sunday = 7
      const dateStr = currentDate.toISOString().split('T')[0];
      
      if (userWorkDays.includes(normalizedDay)) {
        const isHoliday = holidays.some(h => h.date === dateStr);
        if (isHoliday) {
          holidaysFound.push(dateStr);
        } else {
          workingDays++;
        }
        if (blockedDates.includes(dateStr)) {
          blockedDaysFound.push(dateStr);
        }
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }

    if (blockedDaysFound.length > 0) {
      setValidationMessage(`⚠️ Konflikt: ${blockedDaysFound.length} Tag(e) bereits als Urlaub genehmigt!`);
    } else if (holidaysFound.length > 0) {
      setValidationMessage(`ℹ️ ${holidaysFound.length} Feiertag(e) werden nicht vom Urlaubskontingent abgezogen.`);
    } else {
      setValidationMessage('');
    }

    setCalculatedDays(workingDays);
  };

  const validateDateRange = () => {
    const conflicts = [];
    
    const startDate = new Date(formData.startDate);
    const endDate = new Date(formData.endDate);
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      const dateStr = currentDate.toISOString().split('T')[0];
      if (blockedDates.includes(dateStr)) {
        conflicts.push(dateStr);
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return conflicts;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    if (!formData.startDate || !formData.endDate) {
      setMessage({ type: 'error', text: 'Start- und Enddatum sind erforderlich' });
      setLoading(false);
      return;
    }

    const startDate = new Date(formData.startDate);
    const endDate = new Date(formData.endDate);

    if (startDate > endDate) {
      setMessage({ type: 'error', text: 'Startdatum muss vor dem Enddatum liegen' });
      setLoading(false);
      return;
    }

    const conflicts = validateDateRange();
    if (conflicts.length > 0) {
      setMessage({ 
        type: 'error', 
        text: `Konflikt: ${conflicts.length} Tag(e) sind bereits als Urlaub genehmigt. Bitte wählen Sie andere Daten.` 
      });
      setLoading(false);
      return;
    }

    if (calculatedDays && vacationSummary && calculatedDays > vacationSummary.remainingVacationDays) {
      setMessage({ 
        type: 'error', 
        text: `Nicht genügend Urlaubstage verfügbar. Benötigt: ${calculatedDays}, Verfügbar: ${vacationSummary.remainingVacationDays}` 
      });
      setLoading(false);
      return;
    }

    try {
      await timeOffAPI.createVacationRequest({
        startDate: formData.startDate,
        endDate: formData.endDate,
        reason: formData.reason || undefined,
        substituteUserId: formData.substituteUserId ? parseInt(formData.substituteUserId) : undefined
      });

      setMessage({ 
        type: 'success', 
        text: `Urlaubsantrag für ${calculatedDays} Arbeitstage erfolgreich eingereicht` 
      });

      setFormData({
        startDate: '',
        endDate: '',
        reason: '',
        substituteUserId: ''
      });

      loadVacationSummary();
      loadTimeOffRequests();

    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Fehler beim Einreichen des Urlaubsantrags'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      'PENDING': { text: 'Wartend', class: 'pending', icon: <Clock size={16} /> },
      'APPROVED': { text: 'Genehmigt', class: 'approved', icon: <CheckCircle size={16} /> },
      'REJECTED': { text: 'Abgelehnt', class: 'rejected', icon: <XCircle size={16} /> }
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { text: status, class: 'unknown', icon: <HelpCircle size={16} /> };
    return (
      <span className={`status-badge ${statusInfo.class}`}>
        {statusInfo.icon}
        {statusInfo.text}
      </span>
    );
  };

  const getTypeBadge = (type: string) => {
    const typeMap = {
      'VACATION': { text: 'Urlaub', class: 'vacation', icon: <Palmtree size={16} /> },
      'SPECIAL_LEAVE': { text: 'Sonderurlaub', class: 'special-leave', icon: <Target size={16} /> },
      'SICK': { text: 'Krankmeldung', class: 'sick', icon: <Heart size={16} /> }
    };
    
    const typeInfo = typeMap[type as keyof typeof typeMap] || { text: type, class: 'unknown', icon: <HelpCircle size={16} /> };
    return (
      <span className={`type-badge ${typeInfo.class}`}>
        {typeInfo.icon} {typeInfo.text}
      </span>
    );
  };

  const loadBlockedDatesForDatePicker = async (startDate: string, endDate: string) => {
    try {
      const response = await timeOffAPI.getBlockedDates({
        startDate,
        endDate
      });
      setBlockedDates(response.data.blockedDates);
    } catch (error) {
      console.error('Error loading blocked dates for datepicker:', error);
    }
  };

  const isDateBlocked = (dateString: string) => {
    return blockedDates.includes(dateString);
  };

  return (
    <div className="vacation-request">
      <div className="vacation-header">
        <h2>
          <Palmtree size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          Urlaub & Abwesenheit
        </h2>
        
        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
            <button 
              className="message-close" 
              onClick={() => setMessage({ type: '', text: '' })}
            >
              <X size={16} />
            </button>
          </div>
        )}
      </div>

      <div className="vacation-tabs">
        <button 
          className={`tab ${activeTab === 'request' ? 'active' : ''}`}
          onClick={() => setActiveTab('request')}
        >
          Neuer Antrag
        </button>
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Übersicht
        </button>
        <button 
          className={`tab ${activeTab === 'sick-summary' ? 'active' : ''}`}
          onClick={() => setActiveTab('sick-summary')}
        >
          Kranktage-Übersicht
        </button>
      </div>

      <div className="vacation-content">
        {activeTab === 'request' && (
          <div className="vacation-request-form">
            <h3>Neuen Urlaubsantrag stellen</h3>
            
            {vacationSummary && (
              <div className="vacation-info-box">
                <p><strong>Verfügbare Urlaubstage:</strong> {vacationSummary.remainingVacationDays} von {vacationSummary.totalVacationDays}</p>
                <p><strong>Bereits verwendet:</strong> {vacationSummary.usedVacationDays} Tage</p>
                {vacationSummary.pendingVacationDays > 0 && (
                  <p><strong>Wartende Anträge:</strong> {vacationSummary.pendingVacationDays} Tage</p>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} className="vacation-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Startdatum:</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                    className={`date-input ${blockedDates.some(date => date === formData.startDate) ? 'blocked-date' : ''}`}
                    required
                  />
                  {formData.startDate && isDateBlocked(formData.startDate) && (
                    <small className="blocked-date-warning">
                      ⚠️ Dieses Datum ist bereits als Urlaub genehmigt!
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Enddatum:</label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    min={formData.startDate || new Date().toISOString().split('T')[0]}
                    className={`date-input ${blockedDates.some(date => date === formData.endDate) ? 'blocked-date' : ''}`}
                    required
                  />
                  {formData.endDate && isDateBlocked(formData.endDate) && (
                    <small className="blocked-date-warning">
                      ⚠️ Dieses Datum ist bereits als Urlaub genehmigt!
                    </small>
                  )}
                </div>
              </div>

              {/* Anzeige der gesperrten Tage im gewählten Zeitraum */}
              {formData.startDate && formData.endDate && blockedDates.length > 0 && (
                <div className="blocked-dates-info">
                  <h4>
                    <AlertCircle size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                    Bereits belegte Tage in Ihrem gewählten Zeitraum:
                  </h4>
                  <div className="blocked-dates-list">
                    {(() => {
                      const start = new Date(formData.startDate);
                      const end = new Date(formData.endDate);
                      const blockedInRange = [];
                      const currentDate = new Date(start);
                      
                      while (currentDate <= end) {
                        const dateStr = currentDate.toISOString().split('T')[0];
                        if (blockedDates.includes(dateStr)) {
                          blockedInRange.push(dateStr);
                        }
                        currentDate.setDate(currentDate.getDate() + 1);
                      }
                      
                      if (blockedInRange.length === 0) {
                        return (
                          <span className="no-blocked-dates">
                            <CheckCircle size={16} style={{ marginRight: '4px' }} />
                            Keine Konflikte in diesem Zeitraum
                          </span>
                        );
                      }
                      
                      return blockedInRange.map(date => (
                        <span key={date} className="blocked-date-tag">
                          {new Date(date).toLocaleDateString('de-DE')}
                        </span>
                      ));
                    })()}
                  </div>
                </div>
              )}

              {calculatedDays !== null && (
                <div className="calculated-days">
                  <p><strong>Berechnete Arbeitstage:</strong> {calculatedDays} Tage</p>
                  {holidays.length > 0 && (
                    <p className="holiday-info">
                      <Info size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                      {holidays.length} Feiertag(e) werden nicht vom Urlaubskontingent abgezogen
                    </p>
                  )}
                  {validationMessage && (
                    <p className={`validation-message ${validationMessage.includes('Konflikt') ? 'error' : 'info'}`}>
                      {validationMessage}
                    </p>
                  )}
                  {vacationSummary && calculatedDays > vacationSummary.remainingVacationDays && (
                    <p className="warning">
                      <AlertCircle size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                      Nicht genügend Urlaubstage verfügbar!
                    </p>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>Grund (optional):</label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  placeholder="Grund für den Urlaub..."
                  rows={3}
                  maxLength={500}
                />
              </div>

              <div className="form-group">
                <label>Vertretung (optional):</label>
                <select
                  name="substituteUserId"
                  value={formData.substituteUserId}
                  onChange={handleChange}
                >
                  <option value="">Keine Vertretung auswählen</option>
                  {availableSubstitutes.map(substitute => (
                    <option key={substitute.id} value={substitute.id}>
                      {substitute.firstName} {substitute.lastName} ({substitute.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn primary" 
                  disabled={loading || !calculatedDays}
                >
                  {loading ? 'Antrag wird eingereicht...' : 'Urlaubsantrag einreichen'}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'overview' && (
          <div className="vacation-overview">
            {/* Urlaubsübersicht Section */}
            <div className="vacation-summary">
              <h3>Urlaubsübersicht {vacationSummary?.year}</h3>
              
              {vacationSummary && (
                <div className="summary-cards">
                  <div className="summary-card total">
                    <h4>Gesamt Urlaubstage</h4>
                    <p className="big-number">{vacationSummary.totalVacationDays}</p>
                    <small>Für das Jahr {vacationSummary.year}</small>
                  </div>
                  
                  <div className="summary-card used">
                    <h4>Verwendet</h4>
                    <p className="big-number">{vacationSummary.usedVacationDays}</p>
                    <small>Genehmigte Urlaubstage</small>
                  </div>
                  
                  <div className="summary-card pending">
                    <h4>Wartend</h4>
                    <p className="big-number">{vacationSummary.pendingVacationDays}</p>
                    <small>Noch nicht genehmigt</small>
                  </div>
                  
                  <div className="summary-card remaining">
                    <h4>Verbleibend</h4>
                    <p className="big-number">{vacationSummary.remainingVacationDays}</p>
                    <small>Noch verfügbar</small>
                  </div>
                </div>
              )}

              {vacationSummary && (
                <div className="user-info-summary">
                  <h4>Arbeitszeit-Konfiguration</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Wochenstunden:</label>
                      <span>{vacationSummary.user.weeklyHours}h</span>
                    </div>
                    <div className="info-item">
                      <label>Arbeitstage:</label>
                      <span>
                        {vacationSummary.user.workDays.split(',').map(day => {
                          const dayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
                          const dayIndex = parseInt(day) === 7 ? 0 : parseInt(day); // Konvertiert 7 (Sonntag) zu 0
                          return dayNames[dayIndex];
                        }).join(', ')} {}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Meine Anträge Section */}
            <div className="vacation-history">
              <h3>Meine Anträge</h3>
              
              {timeOffRequests.length === 0 ? (
                <p className="no-requests">Noch keine Anträge vorhanden.</p>
              ) : (
                <div className="requests-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Typ</th>
                        <th>Zeitraum</th>
                        <th>Tage</th>
                        <th>Grund</th>
                        <th>Vertretung</th>
                        <th>Eingereicht</th>
                        <th>Status</th>
                        <th>Entschieden von</th>
                        <th>Bemerkungen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timeOffRequests.map(request => (
                        <tr key={request.id}>
                          <td>{getTypeBadge(request.type)}</td>
                          <td>
                            {formatDate(request.startDate)} - {formatDate(request.endDate)}
                          </td>
                          <td>{request.workingDays}</td>
                          
                          <td>{request.reason || '-'}</td>
                          <td>
                            {request.substitute 
                              ? `${request.substitute.firstName} ${request.substitute.lastName}`
                              : '-'
                            }
                          </td>
                          <td>{formatDate(request.createdAt)}</td>
                          <td>{getStatusBadge(request.status)}</td>
                          <td>
                            {request.approver 
                              ? `${request.approver.firstName} ${request.approver.lastName}`
                              : '-'
                            }
                          </td>
                          <td>
                            {request.status === 'REJECTED' && request.adminNotes ? (
                              <div className="admin-notes rejected">
                                <span className="notes-label">Ablehnungsgrund:</span>
                                <span className="notes-text">{request.adminNotes}</span>
                              </div>
                            ) : request.status === 'APPROVED' && request.adminNotes ? (
                              <div className="admin-notes approved">
                                <span className="notes-label">Notiz:</span>
                                <span className="notes-text">{request.adminNotes}</span>
                              </div>
                            ) : request.status === 'REJECTED' && request.approver ? (
                              <div className="admin-notes rejected">
                                <span className="notes-label">Abgelehnt von:</span>
                                <span className="notes-text">{request.approver.firstName} {request.approver.lastName}</span>
                              </div>
                            ) : (
                              '-'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'sick-summary' && (
          <div className="sick-summary">
            <h3>Kranktage-Übersicht {sickDaysSummary?.year}</h3>
            
            {sickDaysSummary && (
              <>
                <div className="summary-cards">
                  <div className="summary-card sick-total">
                    <h4>Kranktage gesamt</h4>
                    <p className="big-number">{sickDaysSummary.totalSickDays}</p>
                    <small>Für das Jahr {sickDaysSummary.year}</small>
                  </div>
                  
                  <div className="summary-card sick-entries">
                    <h4>Krankmeldungen</h4>
                    <p className="big-number">{sickDaysSummary.sickDayEntries}</p>
                    <small>Anzahl Einträge</small>
                  </div>
                </div>

                <div className="sick-days-list">
                  <h4>Krankmeldungen im Detail</h4>
                  {sickDaysSummary.sickDays.length === 0 ? (
                    <p className="no-sick-days">Keine Krankmeldungen in {sickDaysSummary.year}.</p>
                  ) : (
                    <div className="sick-days-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Zeitraum</th>
                            <th>Arbeitstage</th>
                            <th>Grund</th>
                            <th>Ärztliches Attest</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sickDaysSummary.sickDays.map(sick => (
                            <tr key={sick.id}>
                              <td>
                                {formatDate(sick.startDate)} - {formatDate(sick.endDate)}
                              </td>
                              <td>{sick.workingDays} Tage</td>
                              <td>{sick.reason || '-'}</td>
                              <td>
                                {sick.doctorNote ? (
                                  <span className="doctor-note-yes">
                                    <CheckCircle size={16} style={{ marginRight: '4px' }} />
                                    Vorhanden
                                  </span>
                                ) : (
                                  <span className="doctor-note-no">
                                    <XCircle size={16} style={{ marginRight: '4px' }} />
                                    Nicht vorhanden
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="info-box sick-info">
                  <h4>
                    <Info size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                    Hinweis zu Kranktagen
                  </h4>
                  <p>
                    Kranktage werden <strong>nicht</strong> von Ihrem Urlaubskontingent abgezogen, 
                    werden aber in den Stundenübersichten als reguläre Arbeitszeit berücksichtigt.
                  </p>
                  <p>
                    Die Berechnung erfolgt basierend auf Ihren konfigurierten Arbeitstagen 
                    ({vacationSummary?.user.workDays?.split(',').length || 5} Tage/Woche) und Wochenstunden 
                    ({vacationSummary?.user.weeklyHours}h/Woche).
                  </p>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};